import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { updateBillingAddress } from "../../../../redux/slices/projectsSlice";
import type { Customer } from "../../../../types/api";
import { US_STATES, COUNTRIES } from "../../../../constants/address";

interface BillingAddressEditDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
}

const BillingAddressEditDialog = ({
  open,
  customer,
  onClose,
}: BillingAddressEditDialogProps) => {
  const dispatch = useAppDispatch();
  const { updateProjectBillingAddressStatus, updateProjectBillingAddressError } =
    useAppSelector((state) => state.projects);

  const billingAddress = (customer as any)?.billingAddress || {};

  const [formData, setFormData] = useState({
    billAddress1: billingAddress.billAddress1 || "",
    billAddress2: billingAddress.billAddress2 || "",
    billAddressCity: billingAddress.billAddressCity || "",
    billAddressState: billingAddress.billAddressState || "",
    billAddressZip: billingAddress.billAddressZip || "",
    billAddressCountry: billingAddress.billAddressCountry || "US",
  });

  // Reset form when dialog opens with a new customer
  useEffect(() => {
    if (open && customer) {
      const ba = (customer as any)?.billingAddress || {};
      setFormData({
        billAddress1: ba.billAddress1 || "",
        billAddress2: ba.billAddress2 || "",
        billAddressCity: ba.billAddressCity || "",
        billAddressState: ba.billAddressState || "",
        billAddressZip: ba.billAddressZip || "",
        billAddressCountry: ba.billAddressCountry || "US",
      });
    }
  }, [open, customer]);

  const handleChange = (field: string) => (event: any) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!customer?.providerId) return;
    try {
      await dispatch(
        updateBillingAddress({
          projectId: customer.providerId,
          billingAddress: formData,
        }),
      ).unwrap();
      onClose();
    } catch {
      // error handled via Redux state
    }
  };

  const isFormValid =
    formData.billAddress1.trim() &&
    formData.billAddressCity.trim() &&
    formData.billAddressZip.trim();

  const isLoading = updateProjectBillingAddressStatus === "loading";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 0 },
      }}
    >
      <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Edit Billing Address — {customer?.name || ""}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Address Line 1 */}
          <Grid size={12}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>Address Line 1 *</InputLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter street address"
              value={formData.billAddress1}
              onChange={handleChange("billAddress1")}
              disabled={isLoading}
              error={!!updateProjectBillingAddressError}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid size={12}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>Address Line 2 (Optional)</InputLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Apartment, suite, unit, building, floor, etc."
              value={formData.billAddress2}
              onChange={handleChange("billAddress2")}
              disabled={isLoading}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
          </Grid>

          {/* City */}
          <Grid size={6}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>City *</InputLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter city"
              value={formData.billAddressCity}
              onChange={handleChange("billAddressCity")}
              disabled={isLoading}
              error={!!updateProjectBillingAddressError}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
          </Grid>

          {/* ZIP Code */}
          <Grid size={6}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>ZIP/Postal Code *</InputLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter ZIP code"
              value={formData.billAddressZip}
              onChange={handleChange("billAddressZip")}
              disabled={isLoading}
              error={!!updateProjectBillingAddressError}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
          </Grid>

          {/* Country */}
          <Grid size={12}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>Country *</InputLabel>
            <Autocomplete
              value={COUNTRIES.find((c) => c.code === formData.billAddressCountry) || null}
              onChange={(_event, newValue) =>
                handleChange("billAddressCountry")({
                  target: { value: newValue?.code || "" },
                })
              }
              options={COUNTRIES}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              disabled={isLoading}
              fullWidth
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search country..."
                  error={!!updateProjectBillingAddressError}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                />
              )}
            />
          </Grid>

          {/* State */}
          <Grid size={12}>
            <InputLabel sx={{ fontSize: 14, mb: 0.5 }}>State/Province *</InputLabel>
            {formData.billAddressCountry === "US" ? (
              <Autocomplete
                value={US_STATES.find((s) => s.code === formData.billAddressState) || null}
                onChange={(_event, newValue) =>
                  handleChange("billAddressState")({
                    target: { value: newValue?.code || "" },
                  })
                }
                options={US_STATES}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                disabled={isLoading}
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search state..."
                    error={!!updateProjectBillingAddressError}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                placeholder="Enter state/province"
                value={formData.billAddressState}
                onChange={handleChange("billAddressState")}
                disabled={isLoading}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
              />
            )}
          </Grid>
        </Grid>

        {updateProjectBillingAddressError && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
            {updateProjectBillingAddressError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          sx={{ borderRadius: 0, }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isLoading || !isFormValid}
          variant="contained"
          sx={{ borderRadius: 0, }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isLoading ? "Saving..." : "Save Address"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingAddressEditDialog;
